import { useCallback, useId, useState } from 'react';
import { useLocation } from 'react-router-dom';

export interface AppShellLogic {
  /** Whether the off-canvas navigation is showing (small viewports only). */
  isMobileNavOpen: boolean;
  openMobileNav: () => void;
  /** Radix reports both directions through one handler, so the setter is exposed as-is. */
  setMobileNavOpen: (isOpen: boolean) => void;
  /** The content region's id, referenced by the skip link. */
  mainId: string;
  /** The drawer's id, referenced by the topbar trigger's `aria-controls`. */
  mobileNavId: string;
}

/**
 * The shell's own state (U5 — chrome logic lives in a hook, not in the layout).
 *
 * The drawer closes on a route change, and that is the whole reason this is a
 * hook rather than a `useState` in the component: tapping a nav item navigates
 * *without* unmounting the shell, so a drawer that kept its state would sit over
 * the page the operator just asked for — full-screen, on the viewport where they
 * have the least room to work out what happened.
 *
 * The comparison is on `pathname`, so re-entering the active route leaves the
 * drawer alone instead of flickering it shut.
 *
 * It adjusts state **during render** rather than in an effect, which is React's
 * own documented shape for "reset state when a value changes" — and the shape the
 * React Compiler rules require here (a synchronous `setState` inside an effect is
 * an error in this workspace, because it schedules a second render pass for
 * something the first pass already knew).
 */
export function useAppShellLogic(): AppShellLogic {
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const { pathname } = useLocation();
  const [lastPathname, setLastPathname] = useState(pathname);
  const mainId = useId();
  const mobileNavId = useId();

  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setMobileNavOpen(false);
  }

  const openMobileNav = useCallback(() => {
    setMobileNavOpen(true);
  }, []);

  return { isMobileNavOpen, openMobileNav, setMobileNavOpen, mainId, mobileNavId };
}
